import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styles from './PlusNewFeatures.module.scss';
import { ReactComponent as IconTarget } from '../../../../../assets/images/icons/icon_target.svg';
import { ReactComponent as IconWatch } from '../../../../../assets/images/icons/icon_watch.svg';
import { isMobileDevice } from '../../../../../lib/util/isMobileDevice';
import PlusNewFeatureCard from './PlusNewFeatureCard';
import { Carousel } from '../../../../../components/Carousel';
import classNames from 'classnames';

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function getPopupFeatures(t: TFunction): FeatureItem[] {
  return [
    {
      icon: <IconTarget />,
      title: t('2x_improved_gps_tracking'),
      description: t('series_3_plus_outperforms_previous_generations_to_locate_dogs_faster'),
    },
    {
      icon: <IconWatch style={{ fill: 'white' }} />,
      title: t('apple_watch_integration_feature'),
      description: t('no_need_to_take_your_phone_jogging_anymore_just_wear_your_watch'),
    },
  ];
}

/** The five AI behaviors with their gradient icons. */
function getAiFeatures(t: TFunction): FeatureItem[] {
  return [
    {
      icon: <div className={styles.iconEating} />,
      title: t('eating'),
      description: t('stay_tuned_to_shifts_in_appetite_and_keep_track_of_when_theyre_being_fed'),
    },
    {
      icon: <div className={styles.iconDrinking} />,
      title: t('drinking'),
      description: t('know_when_its_time_for_a_water_break_even_before_they_start_panting'),
    },
    {
      icon: <div className={styles.iconBarking} />,
      title: t('barking_feature'),
      description: t('get_notified_of_unusually_vocal_or_quiet_days_to_address_any_stressors'),
    },
    {
      icon: <div className={styles.iconScratching} />,
      title: t('scratching'),
      description: t('keep_tabs_on_your_dogs_skin_health_to_spot_signs_of_irritation_early_on'),
    },
    {
      icon: <div className={styles.iconLicking} />,
      title: t('licking'),
      description: t('track_excessive_licking_to_tackle_sources_of_discomfort'),
    },
  ];
}

export default function PlusNewFeatures({
  containerRef,
  popup = false,
}: {
  containerRef?: React.RefObject<HTMLDivElement>;
  popup?: boolean;
}) {
  const { t } = useTranslation();
  const isMobile = isMobileDevice();
  const popupFeatures = getPopupFeatures(t);
  const aiFeatures = getAiFeatures(t);

  if (popup) {
    return (
      <div ref={containerRef} className={`${styles.container} ${styles.popup}`}>
        <p className={styles.header}>{t('new_on_series_3_plus')}</p>
        <div className={styles.featureListPopup}>
          {popupFeatures.map((feature, index) => (
            <div className={styles.featureItem} key={index}>
              <div className={styles.icon}>{feature.icon}</div>
              <div className={styles.content}>
                <div className={styles.title}>{feature.title}</div>
                <div className={styles.description}>{feature.description}</div>
              </div>
            </div>
          ))}

          <div className={styles.featureItem} key={popupFeatures.length}>
            <div className={styles.content}>
              <div className={styles.title}>{t('ai_behavior_monitoring')}</div>
              <div className={styles.description}>{t('unleash_a_complete_overview_of_their_habits')}</div>
              <div className={styles.featureList}>
                {aiFeatures.map((feature, index) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingLeft: !isMobile && index > 2 ? '60%' : '0px',
                    }}
                    key={index}
                  >
                    {feature.icon}
                    <div className={styles.subtitle}>{feature.title}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div />
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} className={classNames(styles.background, styles.mobileFeatures)}>
        <div className={styles.container}>
          <h2 className={styles.header}>{t('ai_behavior_monitoring_title')}</h2>
          <Carousel className={styles.featureCarousel} freeScroll={true}>
            {aiFeatures.map((feature, index) => (
              <div className={styles.featureCarouselSlide} key={index}>
                <PlusNewFeatureCard icon={feature.icon} title={feature.title} description={feature.description} />
              </div>
            ))}
          </Carousel>
        </div>
      </div>
      <div ref={containerRef} className={classNames(styles.background, styles.desktopFeatures)}>
        <div className={styles.container}>
          <h2 className={styles.header}>{t('ai_behavior_monitoring_title')}</h2>
          <div className={styles.featureList}>
            {aiFeatures.map((feature, index) => (
              <div className={styles.featureDesktopCard} key={index}>
                <PlusNewFeatureCard icon={feature.icon} title={feature.title} description={feature.description} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
